import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAuth } from '../../hooks/useAuth';

interface ProtectedRouteProps {
  /**
   * Skip 2FA check - used for the /setup-2fa route
   */
  skip2FACheck?: boolean;
}

/**
 * Check if user needs to set up 2FA
 * Returns true if:
 * - 2FA is required (via env var)
 * - User has 2FA fields available
 * - 2FA is not enabled
 * - Grace period has expired
 */
const needs2FASetup = (user: { totp_enabled?: boolean; totp_grace_period_ends_at?: string } | null): boolean => {
  // Skip 2FA check if disabled via environment variable
  if (import.meta.env.VITE_TWO_FACTOR_REQUIRED === 'false') return false;

  if (!user) return false;

  // If 2FA is already enabled, no setup needed
  if (user.totp_enabled) return false;

  // If we don't have grace period info, rely on API error handling
  if (!user.totp_grace_period_ends_at) return false;

  // Check if grace period has expired
  const gracePeriodEnd = new Date(user.totp_grace_period_ends_at);
  return new Date() > gracePeriodEnd;
};

/**
 * Protected route wrapper component
 * Redirects to login if user is not authenticated
 * Redirects to 2FA setup if grace period has expired
 * Shows loading spinner while checking auth status
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  skip2FACheck = false,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="loading-container">
        <ProgressSpinner />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if 2FA setup is required (unless skip2FACheck is true)
  if (!skip2FACheck && needs2FASetup(user)) {
    return <Navigate to="/setup-2fa" replace />;
  }

  // Render child routes
  return <Outlet />;
};

export default ProtectedRoute;
